/**
 * Scrub secrets out of free text before it is stored — the console and network trails a
 * reported Issue carries. Runs in the browser (before sending) AND on the server (before
 * storing), so a caller that skips the first pass still cannot store a token.
 *
 * Over-redacting is fine; this text is for a developer reading a trail, not for replay.
 */

const PATTERNS: Array<[RegExp, string]> = [
  // Authorization: Bearer <token> / Basic <creds>
  [/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi, "$1 [redacted]"],
  // JWTs
  [/\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}/g, "[redacted-jwt]"],
  // Delyver-style publishable/secret keys, and other prefixed keys (sk_live_…, pk_test_…)
  [/\b(sk|pk|rk)_(live|test)_[A-Za-z0-9_-]{8,}/g, "[redacted-key]"],
  // key=value / "key": "value" for sensitive-sounding names
  [
    /((?:password|passwd|secret|token|api[_-]?key|access[_-]?key|session|cookie|authorization|signature)["']?\s*[:=]\s*["']?)[^"'&\s,;}]+/gi,
    "$1[redacted]",
  ],
  // OAuth codes and state in a URL query
  [/([?&](?:code|state|token)=)[^&#\s]+/gi, "$1[redacted]"],
  // AWS access key ids
  [/\bAKIA[0-9A-Z]{16}\b/g, "[redacted-aws-key]"],
  // Long hex / base64-ish blobs (presigned signatures, raw tokens)
  [/\b[A-Fa-f0-9]{32,}\b/g, "[redacted-hex]"],
];

export function redactSensitive(text: string): string {
  let out = text;
  for (const [pattern, replacement] of PATTERNS) out = out.replace(pattern, replacement);
  return out;
}

/** Redact, then cap to `max` characters (with an ellipsis when cut). */
export function redactAndCap(text: string, max: number): string {
  const clean = redactSensitive(text);
  return clean.length > max ? `${clean.slice(0, Math.max(0, max - 1))}…` : clean;
}
